import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useDeleteSchool } from '../../hooks/schools/useDeleteSchool';
import { useDeleteAllSchools } from '../../hooks/schools/useDeleteAllSchools';
import { useForceDeleteSchool } from '../../hooks/schools/useForceDeleteSchool';
import { useForceDeleteAllSchools } from '../../hooks/schools/useForceDeleteAllSchools';
import { useRestoreSchool } from '../../hooks/schools/useRestoreSchool';
import { useRestoreAllSchools } from '../../hooks/schools/useRestoreAllSchools';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import SchoolsTableColumns from './Table/SchoolsTableColumns';
import SchoolsTableRow from './Table/SchoolsTableRow';

const SchoolsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const perms = useMemo(() => ({
        list: has('schools_list'),
        create: has('schools_create'),
        edit: has('schools_edit'),
        delete: has('schools_delete'),
        show: has('schools_show'),
        archive_list: has('schools_archive_list'),
        restore: has('schools_restore'),
        force_delete: has('schools_force_delete'),
    }), [has]);

    const { deleteSchool, isDelete } = useDeleteSchool();
    const { deleteAllSchools, isDelete: isDeletingAll } = useDeleteAllSchools();
    const { forceDeleteSchool } = useForceDeleteSchool();
    const { forceDeleteAllSchools } = useForceDeleteAllSchools();
    const { restoreSchool } = useRestoreSchool();
    const { restoreAllSchools } = useRestoreAllSchools();

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl, selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/schools',
        deleteSingle: deleteSchool,
        deleteAll: deleteAllSchools,
        forceDeleteSingle: forceDeleteSchool,
        forceDeleteAll: forceDeleteAllSchools,
        restoreSingle: restoreSchool,
        restoreAll: restoreAllSchools,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, isArchive]);

    const { isLoading, error, schools, meta } = useGetSchools(page, perPage, filters);

    const renderRow = (params) => (
        <SchoolsTableRow
            key={params.key || params.item.id}
            school={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={schools.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {!isArchive && perms.archive_list && (
                    <ArchiveButton
                        to="/schools/archive"
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                )}
                {isArchive && perms.list && (
                    <ArchiveButton
                        to="/schools"
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to List"
                    />
                )}
            </Box>

            <TableLayout
                title={isArabic
                    ? (isArchive ? 'أرشيف المدارس' : 'المدارس')
                    : (isArchive ? 'Schools Archive' : 'Schools')}
                subtitle={isArabic
                    ? (isArchive ? 'إدارة المدارس المحذوفة' : 'إدارة المدارس في النظام')
                    : (isArchive ? 'Manage deleted schools' : 'Manage system schools')}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة مدرسة' : 'Add School'}
                onAddClick={() => navigate('/schools/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading}
                error={error}
                items={schools}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, schools)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={SchoolsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive
                    ? (perms.force_delete ? handleMenuDelete : undefined)
                    : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد مدارس في الأرشيف' : 'لا توجد مدارس'}
                emptyLabelEn={isArchive ? 'No archived schools' : 'No schools'}
                loadErrorAr="حدث خطأ أثناء تحميل المدارس"
                loadErrorEn="Failed to load schools"
            />
        </>
    );
};

export default SchoolsTable;
